import { canShowGalleryItem, canShowWish } from "../core/director/EligibilityEngine";
import { resolveMomentProfile, type MomentProfile } from "../core/director/MomentPresentationResolver";
import type { GalleryItem, RoomSnapshot, TvGalleryTheme, TvWishWallTheme, WishItem } from "../models/contracts";
import { resolveMemoryMode } from "./MemoryDirector";
import { galleryFocusDuration, calculateWishReadingDuration, memoryTiming } from "./memoryTiming";
import { rankGallery, rankWishes } from "./MemorySelectors";
import type { GalleryLayout, MemoryFocus, MemoryHistoryEntry, MemoryStageMode, MemoryStageView, MixedLayout } from "./types";
import { normalizeWishWallTheme, wishWallTheme } from "../wishes/wishWallThemes";

const galleryLayouts: GalleryLayout[] = ["editorial-a", "hero-orbit", "portrait-story", "cinema-strip", "asymmetric", "constellation"];
const mixedLayouts: MixedLayout[] = ["photo-dominant", "balanced", "wish-dominant", "constellation", "farewell"];

const normalizedGalleryTheme = (theme?: TvGalleryTheme): TvGalleryTheme => theme ?? "Automatic";
const galleryLimitForTheme = (theme?: TvGalleryTheme): number => {
  switch (normalizedGalleryTheme(theme)) {
    case "MinimalSpotlight": return 1;
    case "SplitShowcase": return 2;
    case "FeaturedStack":
    case "ClassicGrid": return 4;
    case "MosaicFlow": return 6;
    case "PolaroidMemories": return 5;
    default: return memoryTiming.galleryVisibleMax;
  }
};

const rotate = <T,>(items: T[], offset: number): T[] => {
  if (items.length < 2) return items;
  const start = offset % items.length;
  return [...items.slice(start), ...items.slice(0, start)];
};

export function resolveGalleryLayout(
  theme: TvGalleryTheme | undefined,
  itemCount: number,
  automaticLayout: GalleryLayout,
): GalleryLayout {
  const fallback = itemCount <= 1
    ? "minimal-spotlight"
    : itemCount === 2
      ? "split-showcase"
      : itemCount === 3
        ? "featured-stack"
        : itemCount === 4
          ? "classic-grid"
          : "mosaic-flow";
  switch (theme ?? "Automatic") {
    case "MinimalSpotlight": return "minimal-spotlight";
    case "SplitShowcase": return itemCount >= 2 ? "split-showcase" : fallback;
    case "FeaturedStack": return itemCount >= 3 ? "featured-stack" : fallback;
    case "ClassicGrid": return itemCount >= 4 ? "classic-grid" : fallback;
    case "MosaicFlow": return itemCount >= 5 ? "mosaic-flow" : fallback;
    case "PolaroidMemories": return itemCount >= 3 ? "polaroid-memories" : fallback;
    default: return itemCount <= 2 ? fallback : automaticLayout;
  }
}

export class MemoryStageController {
  private mode: MemoryStageMode | null = null; private layoutIndex = 0; private layoutAt = 0; private generation = 0;
  private wallMode: "gallery" | "wishes" = "gallery"; private wallModeAt = 0;
  private galleryIds: string[] = []; private wishIds: string[] = []; private knownIds = new Set<string>();
  private galleryRotation = 0; private galleryTheme: TvGalleryTheme = "Automatic"; private wishTheme: TvWishWallTheme = "Minimal";
  private wishRotation = 0; private nextWishRotationAt = 0;
  private history: MemoryHistoryEntry[] = []; private focus: MemoryFocus | null = null; private nextFocusAt = 0;
  private pendingHighlight: { type: "gallery" | "wish"; id: string; expiresAt: number } | null = null;
  private arrivalId: string | null = null; private arrivalUntil = 0; private active = false; private lastFocusType: "gallery" | "wish" = "wish";
  private energy: MomentProfile["energy"] = "warm";

  highlight(type: "gallery" | "wish", id: string, now: number, expiresAt = now + 30_000): void {
    if (this.focus && (this.focus.type !== type || this.focus.id !== id)) this.finishFocus(now);
    this.pendingHighlight = { type, id, expiresAt }; this.nextFocusAt = now;
  }
  dismiss(type: "gallery" | "wish", id: string, now: number): void {
    if (this.focus?.type === type && this.focus.id === id) this.finishFocus(now);
    if (type === "gallery") this.galleryIds = this.galleryIds.filter(value => value !== id);
    else this.wishIds = this.wishIds.filter(value => value !== id);
  }

  reconcile(party: RoomSnapshot, now: number, active: boolean): MemoryStageView | null {
    const profile = resolveMomentProfile(party.room.partyType, party.currentMoment?.key);
    this.energy = profile.energy;
    const resolvedMode = resolveMemoryMode(party, profile); if (!resolvedMode) return null;
    const galleryPool = party.tvPublicContent.gallery.filter(item => canShowGalleryItem(item, party)).slice(0, memoryTiming.activePoolLimit);
    const wishPool = party.tvPublicContent.wishes.filter(item => canShowWish(item, party)).slice(0, memoryTiming.activePoolLimit);
    const galleryTheme = normalizedGalleryTheme(party.tvSettings.galleryTheme);
    const wishTheme = normalizeWishWallTheme(party.tvSettings.wishWallTheme);
    const galleryLimit = galleryLimitForTheme(galleryTheme);
    if (galleryTheme !== this.galleryTheme) {
      this.galleryTheme = galleryTheme;
      this.galleryIds = [];
      this.layoutIndex++;
      this.generation++;
      this.layoutAt = now;
      if (galleryTheme !== "Automatic" && this.focus?.type === "gallery" && !this.focus.hostRequested) this.finishFocus(now);
    }
    if (wishTheme !== this.wishTheme) {
      this.wishTheme = wishTheme;
      this.generation++;
      this.layoutAt = now;
      this.nextWishRotationAt = 0;
    }
    const hasBothWalls = galleryPool.length > 0 && wishPool.length > 0;
    let mode: MemoryStageMode = resolvedMode;
    if (hasBothWalls) {
      if (!this.wallModeAt) this.wallModeAt = now;
      const elapsed = now - this.wallModeAt;
      if (elapsed >= memoryTiming.wallLifetimeMs) {
        const switches = Math.floor(elapsed / memoryTiming.wallLifetimeMs);
        if (switches % 2) this.wallMode = this.wallMode === "gallery" ? "wishes" : "gallery";
        this.wallModeAt += switches * memoryTiming.wallLifetimeMs;
      }
      if (this.pendingHighlight) {
        this.wallMode = this.pendingHighlight.type === "gallery" ? "gallery" : "wishes";
        this.wallModeAt = now;
      }
      mode = this.wallMode;
    } else {
      this.wallMode = resolvedMode === "wishes" ? "wishes" : "gallery";
      this.wallModeAt = now;
    }
    const eligibleIds = new Set([...galleryPool.map(item => `g:${item.id}`), ...wishPool.map(item => `w:${item.id}`)]);
    if (this.knownIds.size) {
      const arrived = [...eligibleIds].find(id => !this.knownIds.has(id));
      if (arrived && now >= this.arrivalUntil) {
        this.arrivalId = arrived.slice(2); this.arrivalUntil = now + memoryTiming.arrivalBadgeMs;
        if (arrived.startsWith("g:")) this.galleryIds = this.galleryIds.slice(0, -1);
        else {
          this.wishIds = [...this.wishIds.filter(id => id !== this.arrivalId).slice(0, -1), this.arrivalId];
          this.nextWishRotationAt = 0;
        }
      }
    }
    this.knownIds = eligibleIds;
    if (this.arrivalUntil <= now) this.arrivalId = null;
    if (mode !== this.mode) {
      if (this.focus && this.focus.type !== (mode === "wishes" ? "wish" : "gallery")) this.finishFocus(now);
      if (this.mode !== null && mode === "gallery") {
        this.galleryRotation += galleryLimit;
        this.galleryIds = [];
      }
      this.mode = mode; this.layoutIndex++; this.generation++; this.layoutAt = now;
    }
    if (now - this.layoutAt >= memoryTiming.layoutLifetimeMs[profile.energy]) {
      this.layoutIndex++; this.generation++; this.layoutAt = now;
      this.galleryRotation += galleryLimit;
      this.galleryIds = [];
    }

    const effectiveGalleryLimit = mode === "mixed" ? memoryTiming.mixedGalleryMax : galleryLimit;
    const wishLimit = mode === "mixed" ? memoryTiming.mixedWishMax : wishWallTheme(wishTheme).visibleLimit;
    const rankedGallery = rotate(rankGallery(galleryPool, this.history, now), this.galleryRotation);
    this.galleryIds = this.reconcileIds(this.galleryIds, galleryPool, effectiveGalleryLimit, item => item.id, rankedGallery);
    let rankedWishes = rotate(rankWishes(wishPool, this.history, now), this.wishRotation);
    this.wishIds = this.reconcileIds(this.wishIds, wishPool, wishLimit, item => item.id, rankedWishes);

    const canRotateWishes = mode === "wishes" && active && wishPool.length > wishLimit
      && !this.focus && !this.pendingHighlight;
    if (!canRotateWishes) {
      this.nextWishRotationAt = 0;
    } else if (!this.nextWishRotationAt) {
      this.nextWishRotationAt = now + this.wishRotationDelay(this.byIds(wishPool, this.wishIds));
    } else if (now >= this.nextWishRotationAt) {
      this.wishRotation = (this.wishRotation + 1) % wishPool.length;
      this.wishIds = this.wishIds.slice(1);
      rankedWishes = rotate(rankWishes(wishPool, this.history, now), this.wishRotation);
      this.wishIds = this.reconcileIds(this.wishIds, wishPool, wishLimit, item => item.id, rankedWishes);
      this.generation++;
      this.nextWishRotationAt = now + this.wishRotationDelay(this.byIds(wishPool, this.wishIds));
    }

    if (this.active && !active && this.focus) this.finishFocus(now);
    this.active = active;
    if (this.focus && (this.focus.startedAt + this.focus.durationMs <= now || !eligibleIds.has(`${this.focus.type === "gallery" ? "g" : "w"}:${this.focus.id}`))) this.finishFocus(now);
    if (active && !this.focus) this.maybeFocus(galleryPool, wishPool, profile, now, galleryTheme === "Automatic", wishLimit);

    return {
      party, profile, mode, layout: this.layout(profile, galleryPool.length, party.tvSettings.galleryTheme),
      gallery: mode === "gallery" ? this.byIds(galleryPool, this.galleryIds) : [],
      wishes: mode === "wishes" ? this.byIds(wishPool, this.wishIds) : [],
      focus: this.focus, arrivalId: this.arrivalId, generation: this.generation, suspended: !active,
    };
  }

  private maybeFocus(gallery: GalleryItem[], wishes: WishItem[], profile: MomentProfile, now: number, allowAutomaticGalleryFocus: boolean, wishLimit: number): void {
    if (this.pendingHighlight && this.pendingHighlight.expiresAt <= now) this.pendingHighlight = null;
    let type = this.pendingHighlight?.type;
    const id = this.pendingHighlight?.id;
    const hostRequested = Boolean(type && id);
    if (!hostRequested && now < this.nextFocusAt) return;
    if (!type) {
      if (this.mode === "gallery") type = "gallery";
      else if (this.mode === "wishes") type = "wish";
      else type = this.lastFocusType === "gallery" && wishes.length ? "wish" : gallery.length ? "gallery" : "wish";
    }
    if (type === "gallery" && !hostRequested && !allowAutomaticGalleryFocus) {
      this.nextFocusAt = now + memoryTiming.focusGapMs[profile.energy];
      return;
    }
    if (type === "wish" && !hostRequested) {
      this.nextFocusAt = now + memoryTiming.focusGapMs[profile.energy];
      return;
    }
    if (type === "gallery") { const item = id ? gallery.find(value => value.id === id) : rankGallery(gallery, this.history, now)[0]; if (item) { this.focus = { type, id: item.id, startedAt: now, durationMs: galleryFocusDuration(item), hostRequested }; this.lastFocusType = type; this.galleryIds = this.includeFocused(this.galleryIds, item.id, memoryTiming.galleryVisibleMax); } }
    else { const item = id ? wishes.find(value => value.id === id) : rankWishes(wishes, this.history, now)[0]; if (item) { this.focus = { type, id: item.id, startedAt: now, durationMs: calculateWishReadingDuration(item.text), hostRequested }; this.lastFocusType = type; this.wishIds = this.includeFocused(this.wishIds, item.id, wishLimit); } }
    this.pendingHighlight = null;
    if (!this.focus) this.nextFocusAt = now + 5_000;
  }

  private finishFocus(now: number): void { if (this.focus) this.history.push({ type: this.focus.type, id: this.focus.id, shownAt: now }); this.history = this.history.slice(-memoryTiming.focusHistoryLimit); this.focus = null; this.nextFocusAt = now + memoryTiming.focusGapMs[this.energy]; }
  private layout(profile: MomentProfile, galleryCount: number, galleryTheme?: TvGalleryTheme): GalleryLayout | MixedLayout { if (this.mode === "mixed") return profile.memoryAffinity === "dominant" ? "farewell" : mixedLayouts[this.layoutIndex % mixedLayouts.length]!; const automatic = galleryLayouts[this.layoutIndex % galleryLayouts.length]!; return resolveGalleryLayout(galleryTheme, galleryCount, automatic); }
  private reconcileIds<T>(current: string[], eligible: T[], limit: number, id: (value: T) => string, ranked: T[]): string[] { const allowed = new Set(eligible.map(id)); const kept = current.filter(value => allowed.has(value)).slice(0, limit); for (const item of ranked) { const value = id(item); if (kept.length >= limit) break; if (!kept.includes(value)) kept.push(value); } return kept; }
  private byIds<T extends { id: string }>(items: T[], ids: string[]): T[] { const map = new Map(items.map(item => [item.id, item])); return ids.map(id => map.get(id)).filter((value): value is T => Boolean(value)); }
  private includeFocused(ids: string[], id: string, limit: number): string[] { return ids.includes(id) ? ids : [id, ...ids].slice(0, limit); }
  private wishRotationDelay(wishes: WishItem[]): number {
    const readingTime = wishes.reduce((longest, wish) => Math.max(longest, calculateWishReadingDuration(wish.text)), 0);
    return Math.max(memoryTiming.wishRotationMinMs,
      Math.min(memoryTiming.wishRotationMaxMs, readingTime + memoryTiming.wishRotationReadingBufferMs));
  }
}
